import { QueryFactory } from '@lmt/shared-types';
import {
  DeviceModel,
  CreateMeasurementMappingDto,
  UpdateMeasurementMappingDto,
} from '@lmt/shared-types';

import { Loading } from '@lmt/unicorn-shared/app/components/common/Loading';
import {
  createMapping,
  editMapping,
  removeMapping,
} from '@lmt/unicorn-shared/app/features/mappings/operations';
import { useDataGridExportHandler } from '@lmt/unicorn-shared/app/helpers/devextreme-helper';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';
import { mapExcelRowToDataGrid } from '@lmt/unicorn-shared/app/helpers/excel-to-grid-helper';
import { convertToErrorString } from '@lmt/unicorn-shared/app/helpers/form/useErrorHandler';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { SavingEvent } from 'devextreme/ui/data_grid';
import React, { FC, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { SourceAutocomplete } from './SourceAutocomplete';
import {
  useDataGrid,
  DXG,
  DataGrid,
} from '@lmt/unicorn-shared/app/common/devexpress/Grid/dx-grid';
import { useEventCallback } from '@mui/material';
import { readFileAsExcel } from '@lmt/unicorn-shared/app/helpers/file-helper';

export interface MappingRule {
  id: number;
  mappingId?: number;
  isActive: boolean;
  sourceName: string;
  comment: string;
  targetName: string;
  isMandatory: boolean;
  isDefault: boolean;
  ignoreErrors: boolean;
}

interface TableProps {
  measurementTypeId: number;
  deviceModel: DeviceModel;
}

export const MappingsTable: FC<TableProps> = (props) => {
  const { measurementTypeId, deviceModel } = props;
  const queryClient = useQueryClient();
  useDataGrid();
  const i18next = useScopedTranslation('mappings.table');

  const modals = useModal();

  const fieldsQuery = QueryFactory.DataBlockFieldQuery.useGetQuery({
    dataBlockTypeId: measurementTypeId,
  });

  const mappingsQuery = QueryFactory.MeasurementMappingQuery.useGetQuery(
    measurementTypeId,
    deviceModel,
  );

  const mappingRules: MappingRule[] = useMemo(() => {
    const measurementFields = fieldsQuery.data ?? [];
    const mappings = mappingsQuery.data ?? [];
    return measurementFields
      .filter((field) => !field.isDeactivated)
      .map((field) => {
        const mapping = mappings.find(
          (mapping) => mapping.fieldId === field.id,
        );
        return {
          id: field.id,
          mappingId: mapping?.id,
          isActive: mapping?.isActive || false,
          sourceName: mapping?.sourceName || '',
          comment: mapping?.comment || '',
          targetName: field.name || '',
          isMandatory: mapping?.isMandatory || false,
          isDefault: mapping?.isDefault || false,
          ignoreErrors: mapping?.ignoreErrors || false,
        };
      });
  }, [fieldsQuery.data, mappingsQuery.data]);

  const refetchQuery = async () => {
    const state = dataGridRef.current?.instance().state();
    await mappingsQuery.refetch();
    await queryClient.invalidateQueries({
      queryKey: QueryFactory.DataBlockFieldQuery.getQueryKey(measurementTypeId),
    });
    dataGridRef.current?.instance().state(state);
  };

  const [isSaving, setIsSaving] = useState(false);

  const saveChanges = useEventCallback(
    async (props: SavingEvent<MappingRule, number>) => {
      const changes = props.changes;
      if (!changes.length) {
        return;
      }
      try {
        setIsSaving(true);
        for (const change of changes) {
          const rowId = change.key;
          const rule = mappingRules.find((x) => x.id === rowId);
          if (!rule) {
            continue;
          }
          const newData = change.data;
          if (rule.mappingId) {
            if (newData.sourceName === null || newData.sourceName === '') {
              // remove mapping
              await removeMapping(rule.mappingId);
            } else {
              const dto: UpdateMeasurementMappingDto = {
                sourceName: newData.sourceName || rule.sourceName,
                comment:
                  newData.comment === '' ? '' : newData.comment || rule.comment,
                isActive:
                  newData.isActive === undefined
                    ? rule.isActive
                    : newData.isActive,
                isMandatory:
                  newData.isMandatory === undefined
                    ? rule.isMandatory
                    : newData.isMandatory,
                ignoreErrors:
                  newData.ignoreErrors === undefined
                    ? rule.ignoreErrors
                    : newData.ignoreErrors,
              };
              await editMapping(rule.mappingId, dto);
            }
          } else {
            // create new mapping if source name is set
            if (newData.sourceName) {
              const dto: CreateMeasurementMappingDto = {
                sourceName: newData.sourceName,
                fieldId: rule.id,
                comment:
                  newData.comment === null
                    ? ''
                    : newData.comment || rule.comment,
                measurementTypeId: measurementTypeId,
                deviceModel: deviceModel!,
                isActive: !!newData.isActive,
                isMandatory: !!newData.isMandatory,
                ignoreErrors: !!newData.ignoreErrors,
              };
              await createMapping(dto);
            }
          }
        }
        await refetchQuery();
      } catch (e) {
        void modals.showError({ text: convertToErrorString(e) });
        props.cancel = true;
      } finally {
        setIsSaving(false);
      }
    },
  );

  const inputFileRef = useRef<HTMLInputElement>(null);
  const [isExcelLoading, setIsExcelLoading] = useState(false);

  async function onImportExcelFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target!.files![0];
    if (!file) {
      return;
    }
    setIsExcelLoading(true);
    try {
      const values = await readFileAsExcel(file);

      const dataGrid = dataGridRef.current!.instance();
      const data: MappingRule[] = await dataGrid.getDataSource().load();
      for (let i = 2; i < values.length; i++) {
        const row = values[i];
        try {
          const newRow = await mapExcelRowToDataGrid<MappingRule>(
            dataGrid,
            row,
          );
          const rowIndex = data.findIndex(
            (x) => x.targetName === newRow.targetName,
          );
          if (rowIndex > -1) {
            Object.keys(newRow).forEach((key) => {
              const newValue = (newRow as any)[key];
              const currentValue = (data[rowIndex] as any)[key];
              if (currentValue !== newValue) {
                dataGrid.cellValue(rowIndex, key, newValue);
              }
            });
          }
        } catch (e) {
          await modals.showError({
            text: `Error parsing excel row ${i}: ${convertToErrorString(e)}`,
          });
          return;
        }
      }
    } finally {
      setIsExcelLoading(false);
      // to allow choosing the same file again
      inputFileRef.current!.value = '';
    }
  }

  const onExportHandler = useDataGridExportHandler({ fileName: 'Mappings' });

  const dataGridRef = useRef<DataGrid<MappingRule, number>>(null);

  return (
    <Loading
      loading={isExcelLoading || isSaving}
      title={
        isExcelLoading ? i18next.t('loading_excel') : i18next.t('loading_save')
      }
      visibleIfOverflow={true}
    >
      <input
        type="file"
        id="importExcelFile"
        ref={inputFileRef}
        className={'hidden'}
        onChange={onImportExcelFile}
      />
      <DXG.DataGrid
        ref={dataGridRef}
        onSaving={(props) => {
          props.promise = saveChanges(props);
        }}
        dataSource={mappingRules}
        keyExpr="id"
        showBorders={true}
        export={{
          enabled: true,
        }}
        onExporting={onExportHandler}
        onEditorPreparing={(e) => {
          // if field is mandatory then 'ignoreErrors' should be disabled
          if (e.dataField === 'ignoreErrors' && e.parentType == 'dataRow') {
            e.editorOptions.disabled = e.row!.data && e.row!.data.isMandatory;
          }
        }}
        onOptionChanged={(props) => {
          if (
            props.name === 'columns' &&
            props.fullName?.includes('sortOrder') &&
            !props.value
          ) {
            props.component?.columnOption('id', 'sortOrder', 'desc');
          }
        }}
        onToolbarPreparing={(e) => {
          e.toolbarOptions!.items!.push({
            location: 'after',
            widget: 'dxButton',
            options: {
              width: 136,
              text: i18next.t('import_button'),
              onClick: () => inputFileRef.current!.click(),
            },
          });
        }}
      >
        <DXG.Paging enabled={false} />
        <DXG.Editing
          mode="batch"
          allowUpdating={true}
          selectTextOnEditStart={true}
          startEditAction="click"
        />
        <DXG.SearchPanel
          visible={true}
          width={240}
          placeholder={i18next.t('search_placeholder')}
        />
        <DXG.Sorting mode="single" />
        <DXG.Column
          dataField="id"
          sortIndex={0}
          defaultSortOrder="desc"
          sortOrder="desc"
          visible={false}
        />
        <DXG.Column
          width={120}
          dataField="isActive"
          caption={i18next.t('is_active')}
          dataType="boolean"
        />
        <DXG.Column
          width={120}
          dataField="isMandatory"
          caption={i18next.t('is_mandatory')}
          dataType="boolean"
          setCellValue={(rowData: any, value: boolean) => {
            rowData.isMandatory = value;
            if (value) {
              rowData.ignoreErrors = false;
            }
          }}
        />
        <DXG.Column
          width={120}
          dataField="ignoreErrors"
          caption={i18next.t('ignore_errors')}
          dataType="boolean"
        />
        <DXG.Column
          width={200}
          dataField="sourceName"
          caption={i18next.t('source_name')}
          dataType="string"
          showEditorAlways={true}
          editCellComponent={SourceAutocomplete}
        >
          <DXG.CustomRule
            message={i18next.t('required_for_active_rule')}
            validationCallback={(item: any) => {
              const data = item.data as MappingRule;
              // source name cannot be empty for active mappings
              return !(data.isActive && !data.sourceName);
            }}
            reevaluate={true}
          />
        </DXG.Column>
        <DXG.Column
          width={270}
          dataField="targetName"
          caption={i18next.t('target_name')}
          allowEditing={false}
          dataType="string"
        />
        <DXG.Column
          dataField="comment"
          caption={i18next.t('comment')}
          dataType="string"
        />
      </DXG.DataGrid>
    </Loading>
  );
};
